import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { EVENTS_STRINGS } from '../constants/events-strings';
import { guestUnregisterErrorMessage } from '../guest-registration-error';
import { useGuestUnregister } from '../hooks/useGuestUnregister';
import { UnregisterConfirmDialog } from './UnregisterConfirmDialog';

interface GuestUnregisterCTAProps {
  eventId: string;
  /** The remembered registration token; sent in a header, never shown or logged. */
  token: string;
  /** Called after a successful cancellation, once the remembered entry is cleared. */
  onUnregistered: () => void;
}

/**
 * Unregister for a remembered non-member (FR-289): reuses
 * `UnregisterConfirmDialog`. A failure shows inside the open dialog with the
 * stored entry untouched and the action retryable; closing without confirming
 * changes nothing. The dialog cannot be dismissed mid-request, or the outcome
 * would be lost.
 */
export function GuestUnregisterCTA({ eventId, token, onUnregistered }: GuestUnregisterCTAProps) {
  const [open, setOpen] = useState(false);
  const unregister = useGuestUnregister(eventId, onUnregistered);

  const handleOpenChange = (next: boolean) => {
    if (!next && unregister.isPending) return;
    // A failure from an earlier attempt must not greet the next time it opens.
    if (!next) unregister.reset();
    setOpen(next);
  };

  return (
    <>
      <Button variant="chip-outline" className="w-full" onClick={() => setOpen(true)}>
        {EVENTS_STRINGS.GUEST.UNREGISTER}
      </Button>
      <UnregisterConfirmDialog
        open={open}
        onOpenChange={handleOpenChange}
        onConfirm={() => unregister.mutate(token)}
        isPending={unregister.isPending}
        error={unregister.isError ? guestUnregisterErrorMessage(unregister.error) : undefined}
      />
    </>
  );
}
